import { IconElement } from 'types/IconElement';
import { InformationIcon } from 'components/icons';
import Card, { CardProps } from 'components/atoms/Card/Card';
import classes from 'classnames';
import Link from 'components/atoms/Link/Link';
import React, { memo, PropsWithChildren } from 'react';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './FormWarning.module.scss';
import Text from 'components/atoms/Typography/Text';

export type FormWarningProps = CardProps & {
  text?: string;
  title?: string;
  linkHref?: string;
  linkText?: string;
  icon?: IconElement;
  className?: string;
  headingClassName?: string;
  textContentClassName?: string;
  iconClassName?: string;
  dataTest?: string;
};

export default memo(function FormWarning({
  text,
  title,
  children,
  icon,
  linkHref,
  linkText,
  intent = 'warning',
  className,
  textContentClassName,
  headingClassName,
  dataTest = 'formWarning',
  ...props
}: PropsWithChildren<FormWarningProps>) {
  return (
    <Card
      className={classes(styles['wrapper'], className)}
      intent={intent}
      {...props}
    >
      {children ? (
        children
      ) : (
        <div data-test={dataTest}>
          <Spacer
            marginBottom={icon ? '0px' : '10px'}
            className={classes(
              styles.heading,
              icon && styles['heading-custom-icon'],
              headingClassName,
            )}
          >
            {icon ? (
              <span className={styles['custom-icon']}>{icon}</span>
            ) : (
              <span className={styles.icon}>{<InformationIcon />}</span>
            )}
            {title && (
              <Text
                weight="medium"
                level={1}
                className={classes(
                  styles.title,
                  icon && styles['custom-title'],
                )}
                dangerouslySetInnerHTML={{ __html: title }}
                dataTest={`${dataTest}-title`}
              />
            )}
          </Spacer>
          <div
            className={classes(
              styles['text-content'],
              icon && styles['text-content-custom-icon'],
              textContentClassName,
            )}
          >
            {text && (
              <Text
                level={2}
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: text }}
                dataTest={`${dataTest}-text`}
              />
            )}
            {linkHref && linkText && (
              <Link
                href={linkHref}
                className={styles['link']}
                dataTest={`${dataTest}-link`}
              >
                {linkText}
              </Link>
            )}
          </div>
        </div>
      )}
    </Card>
  );
});
